import { useEffect, useState, useRef } from "react";

import {
  MapContainer,
  TileLayer,
  Marker,
  useMap,
  useMapEvents,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";

import {
  updateSectionContent,
  updateSectionItem,
  createSectionItem,
  deleteSectionItem,
} from "../../../../../api/storeManagement/sections";

import { uploadImage } from "../../../../../api/storeManagement/upload";

import { API_BASE_URL } from "../../../../../api/http";

import {
  Icon,
  PLUS_ICON,
  TRASH_ICON,
  CHECK_ICON,
  IMAGE_ICON,
} from "../shared/EditShopShared";

import "../../../../../styles/panel/user/manageShop/tabs/SectionEditor.css";
import toast, { Toaster } from "react-hot-toast";

/*
 * آیکون پیش‌فرض Leaflet با Webpack/Vite درست لود نمی‌شود،
 * پس دستی ست‌اش می‌کنیم.
 */
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

/* مرکز پیش‌فرض نقشه: تهران */
const DEFAULT_MAP_CENTER = [35.6892, 51.389];

/*
 * انتخاب مقصد از روی نقشه با کلیک
 */
function MapClickHandler({ onSelect }) {
  useMapEvents({
    click(e) {
      onSelect({ lat: e.latlng.lat, lng: e.latlng.lng });
    },
  });

  return null;
}

/*
 * وقتی موقعیت از نتیجه‌ی سرچ انتخاب می‌شود،
 * نقشه باید به همان نقطه پرواز کند.
 */
function MapFlyTo({ lat, lng }) {
  const map = useMap();

  useEffect(() => {
    if (typeof lat === "number" && typeof lng === "number") {
      map.flyTo([lat, lng], 15);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lat, lng]);

  return null;
}

/*
 * جستجوی آدرس (با سرویس Nominatim متعلق به OpenStreetMap)
 * با انتخاب یکی از نتایج، هم موقعیت روی نقشه ست می‌شود
 * و هم متن کامل آدرس انتخاب‌شده ذخیره می‌شود.
 */
function MapAddressSearch({ onSelect }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const debounceRef = useRef(null);
  const requestIdRef = useRef(0);
  const containerRef = useRef(null);

  /*
   * بستن لیست با کلیک بیرون از باکس سرچ.
   * به‌جای onBlur روی اینپوت، چون لایه‌های داخلی Leaflet
   * z-index بالایی دارند و اعتماد به blur/timeout
   * می‌تواند با کلیک روی آیتم لیست تداخل پیدا کند.
   */
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);

    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (!query || query.trim().length < 3) {
      setResults([]);
      setLoading(false);
      return;
    }

    const currentRequestId = ++requestIdRef.current;

    debounceRef.current = setTimeout(async () => {
      try {
        setLoading(true);

        const res = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&accept-language=fa&limit=6&q=${encodeURIComponent(
            query,
          )}`,
        );

        const data = await res.json();

        if (currentRequestId === requestIdRef.current) {
          setResults(Array.isArray(data) ? data : []);
          setOpen(true);
        }
      } catch (err) {
        console.error(err);

        if (currentRequestId === requestIdRef.current) {
          setResults([]);
        }
      } finally {
        if (currentRequestId === requestIdRef.current) {
          setLoading(false);
        }
      }
    }, 450);

    return () => clearTimeout(debounceRef.current);
  }, [query]);

  const handleSelect = (item) => {
    onSelect({
      lat: parseFloat(item.lat),
      lng: parseFloat(item.lon),
      address: item.display_name,
    });

    setQuery(item.display_name);
    setResults([]);
    setOpen(false);
  };

  return (
    <div className="se-map-search" ref={containerRef}>
      <input
        type="text"
        className="se-map-search-input"
        placeholder="جستجوی آدرس..."
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onFocus={() => results.length > 0 && setOpen(true)}
      />

      {loading && <div className="se-map-search-status">در حال جستجو...</div>}

      {open && !loading && results.length > 0 && (
        <ul className="se-map-search-results">
          {results.map((item) => (
            <li
              key={item.place_id}
              className="se-map-search-result"
              onMouseDown={() => handleSelect(item)}
            >
              {item.display_name}
            </li>
          ))}
        </ul>
      )}

      {open && !loading && query.trim().length >= 3 && results.length === 0 && (
        <div className="se-map-search-status">نتیجه‌ای پیدا نشد.</div>
      )}
    </div>
  );
}

function MapPicker({ value, onChange }) {
  const [addressLoading, setAddressLoading] = useState(false);

  const reverseRequestIdRef = useRef(0);

  const hasPosition =
    value && typeof value.lat === "number" && typeof value.lng === "number";

  const position = hasPosition ? [value.lat, value.lng] : null;

  /*
   * وقتی کاربر روی نقشه کلیک می‌کند:
   * اول مختصات فوری ست می‌شود تا مارکر جابه‌جا شود،
   * بعد با reverse geocoding آدرس همان نقطه گرفته و نوشته می‌شود.
   */
  const handleMapClick = async (coords) => {
    onChange({ lat: coords.lat, lng: coords.lng });

    const requestId = ++reverseRequestIdRef.current;

    setAddressLoading(true);

    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${coords.lat}&lon=${coords.lng}&accept-language=fa`,
      );

      const data = await res.json();

      if (requestId === reverseRequestIdRef.current && data && !data.error) {
        onChange({
          lat: coords.lat,
          lng: coords.lng,
          address: data.display_name,
        });
      }
    } catch (err) {
      console.error(err);
    } finally {
      if (requestId === reverseRequestIdRef.current) {
        setAddressLoading(false);
      }
    }
  };

  return (
    <div className="se-map-picker">
      <MapAddressSearch onSelect={(result) => onChange({ ...result })} />

      <MapContainer
        center={position || DEFAULT_MAP_CENTER}
        zoom={position ? 14 : 5}
        scrollWheelZoom
        className="se-map-container"
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
        />

        <MapClickHandler onSelect={handleMapClick} />

        <MapFlyTo lat={value?.lat} lng={value?.lng} />

        {position && <Marker position={position} />}
      </MapContainer>

      <div className="se-map-coords">
        {hasPosition ? (
          <>
            <span>عرض جغرافیایی: {value.lat.toFixed(6)}</span>
            <span>طول جغرافیایی: {value.lng.toFixed(6)}</span>
          </>
        ) : (
          <span>
            برای انتخاب مقصد، آدرس را جستجو کنید یا روی نقشه کلیک کنید.
          </span>
        )}
      </div>

      {addressLoading && (
        <div className="se-map-address se-map-address--loading">
          در حال دریافت آدرس...
        </div>
      )}

      {!addressLoading && value?.address && (
        <div className="se-map-address">{value.address}</div>
      )}
    </div>
  );
}

export default function SectionEditor({
  section,
  categories = [],
  collections = [],
  showcaseOrder,
}) {
  const template = section?.template_section;

  const config = template?.config_json || {};

  const title = config.label || section?.section_key || "سکشن";

  const fields = config.fields || [];

  const repeatable = Boolean(template?.repeatable);

  const isProductShowcase = Boolean(
    section?.section_key?.startsWith("product_showcase"),
  );

  const [content, setContent] = useState(section?.content_json || {});

  const [items, setItems] = useState(section?.items || []);

  const [parentCategoryId, setParentCategoryId] = useState(
    section?.content_json?.parent_category_id || null,
  );

  const [saving, setSaving] = useState(false);

  /*
   * آیتم‌های repeatable که تغییر کرده‌اند
   * ولی هنوز ذخیره نشده‌اند
   */
  const [dirtyItemIds, setDirtyItemIds] = useState(() => new Set());

  /*
   * استخراج پیام خطا از پاسخ سرور
   */
  const getErrorMessage = (err, fallback) => {
    return (
      err?.response?.data?.message ||
      err?.response?.data?.error ||
      err?.response?.data?.detail ||
      (typeof err?.response?.data === "string" ? err.response.data : null) ||
      err?.message ||
      fallback
    );
  };

  /*
   * اگر section عوض شد،
   * state داخلی هم باید عوض شود.
   */
  useEffect(() => {
    setContent(section?.content_json || {});
    setItems(section?.items || []);

    setParentCategoryId(section?.content_json?.parent_category_id || null);
  }, [section]);

  /*
   * ساخت URL تصویر
   */
  const getImageUrl = (url) => {
    if (!url) return "";

    if (url.startsWith("http://") || url.startsWith("https://")) {
      return url;
    }

    return `${API_BASE_URL}${url}`;
  };

  /*
   * تغییر content_json
   */
  const handleContentChange = (name, value) => {
    setContent((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  /*
   * ذخیره Section معمولی
   */
  const saveSection = async () => {
    try {
      setSaving(true);

      await updateSectionContent(section.section_id, content);

      toast.success("سکشن با موفقیت ذخیره شد");
    } catch (err) {
      console.error(err);
      toast.error(getErrorMessage(err, "ذخیره سکشن با خطا مواجه شد"));
    } finally {
      setSaving(false);
    }
  };

  /*
   * افزودن Item برای repeatable
   */
  const addItem = async () => {
    try {
      const res = await createSectionItem(section.section_id, {});

      setItems((prev) => [...prev, res.data]);
    } catch (err) {
      console.error(err);
      toast.error(getErrorMessage(err, "افزودن آیتم با خطا مواجه شد"));
    }
  };

  /*
   * تغییر Item
   */
  const updateItemField = (itemId, fieldName, value) => {
    setItems((prev) =>
      prev.map((item) =>
        item.item_id === itemId
          ? {
              ...item,
              content_json: {
                ...(item.content_json || {}),
                [fieldName]: value,
              },
            }
          : item,
      ),
    );

    setDirtyItemIds((prev) => {
      const next = new Set(prev);
      next.add(itemId);
      return next;
    });
  };

  /*
   * ذخیره Item
   */
  const saveItem = async (item) => {
    try {
      await updateSectionItem(item.item_id, item.content_json || {});

      setDirtyItemIds((prev) => {
        const next = new Set(prev);
        next.delete(item.item_id);
        return next;
      });

      toast.success("آیتم با موفقیت ذخیره شد");
    } catch (err) {
      console.error(err);
      toast.error("error", getErrorMessage(err, "ذخیره آیتم با خطا مواجه شد"));
    }
  };

  /*
   * حذف Item
   */
  const removeItem = async (itemId) => {
    try {
      await deleteSectionItem(itemId);

      setItems((prev) => prev.filter((item) => item.item_id !== itemId));

      setDirtyItemIds((prev) => {
        const next = new Set(prev);
        next.delete(itemId);
        return next;
      });

      toast.success("آیتم با موفقیت حذف شد");
    } catch (err) {
      console.error(err);
      toast.error(getErrorMessage(err, "حذف آیتم با خطا مواجه شد"));
    }
  };

  /*
   * آپلود تصویر
   */
  const handleImageUpload = async (event, onChange) => {
    const file = event.target.files?.[0];

    if (!file) return;

    const formData = new FormData();

    formData.append("image", file);

    try {
      const res = await uploadImage(formData);

      onChange(res.data.url);

      toast.success("تصویر با موفقیت آپلود شد");
    } catch (err) {
      console.error(err);
      toast.error(getErrorMessage(err, "آپلود تصویر با خطا مواجه شد"));
    }
  };

  /*
   * تغییر Array
   */
  const renderArrayField = (field, value, onChange) => {
    const arrayValue = Array.isArray(value) ? value : [];

    const addArrayItem = () => {
      const newItem = {};

      (field.items || []).forEach((subField) => {
        newItem[subField.name] = subField.type === "number" ? null : "";
      });

      onChange([...arrayValue, newItem]);
    };

    const updateArrayItem = (index, fieldName, fieldValue) => {
      const updated = arrayValue.map((item, itemIndex) =>
        itemIndex === index
          ? {
              ...item,
              [fieldName]: fieldValue,
            }
          : item,
      );

      onChange(updated);
    };

    const deleteArrayItem = (index) => {
      onChange(arrayValue.filter((_, i) => i !== index));
    };

    return (
      <div className="se-array">
        <Toaster
          position="top-center"
          toastOptions={{
            duration: 3000,
            style: {
              fontFamily: "inherit",
              direction: "rtl",
              fontSize: "13.5px",
            },
          }}
        />
        {arrayValue.length === 0 && (
          <div className="se-empty">هنوز آیتمی اضافه نشده است.</div>
        )}

        <div className="se-array-list">
          {arrayValue.map((item, index) => (
            <div className="se-array-item" key={index}>
              <div className="se-array-head">
                <span className="se-badge">آیتم {index + 1}</span>

                <button
                  type="button"
                  className="esp-icon-btn esp-icon-btn--remove"
                  onClick={() => deleteArrayItem(index)}
                  title="حذف"
                >
                  <Icon>{TRASH_ICON}</Icon>
                </button>
              </div>

              {renderFieldsGroup(
                field.items || [],
                (subField) => item[subField.name],
                (subField) => (value) =>
                  updateArrayItem(index, subField.name, value),
              )}
            </div>
          ))}
        </div>

        <button type="button" className="esp-add-line" onClick={addArrayItem}>
          <Icon>{PLUS_ICON}</Icon>
          افزودن آیتم
        </button>
      </div>
    );
  };

  /*
   * Renderer اصلی Field
   */
  const renderField = (field, value, onChange) => {
    if (field.type === "array") {
      return renderArrayField(field, value, onChange);
    }

    switch (field.type) {
      case "textarea":
        return (
          <textarea
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value)}
          />
        );

      case "number":
        return (
          <input
            type="number"
            value={value ?? ""}
            onChange={(e) =>
              onChange(e.target.value === "" ? null : Number(e.target.value))
            }
          />
        );

      case "image":
        return (
          <label className={`se-image-dropzone${value ? " has-image" : ""}`}>
            <input
              type="file"
              accept="image/*"
              className="se-image-input "
              onChange={(e) => handleImageUpload(e, onChange)}
            />

            {value ? (
              <img
                src={getImageUrl(value)}
                alt=""
                className="se-image-preview"
              />
            ) : (
              <span className="se-image-placeholder ">
                <Icon className={"esp-img-upload-empty"}>{IMAGE_ICON}</Icon>
                <span className="esp-img-upload-empty span">آپلود عکس</span>
              </span>
            )}
          </label>
        );

      case "url":
        return (
          <input
            type="url"
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value)}
          />
        );

      case "map":
        return <MapPicker value={value} onChange={onChange} />;

      case "select": {
        let options = field.options || [];

        const isParentCategory = field.source === "categories_parent";

        const isChildCategory = field.source === "categories_child";

        const isCollection = field.source === "collections";

        if (isParentCategory) {
          options = categories
            .filter((category) => !category.parent)
            .map((category) => ({
              value: category.id,
              label: category.name,
            }));
        }

        if (isChildCategory) {
          const parentId =
            content.parent_category_id || parentCategoryId || null;

          const childCategories = parentId
            ? categories.filter(
                (category) => Number(category.parent) === Number(parentId),
              )
            : categories.filter((category) => category.parent);

          options = childCategories.map((category) => ({
            value: category.id,
            label: category.name,
          }));
        }

        if (isCollection) {
          options = collections.map((collection) => ({
            value: collection.id,
            label: collection.name || collection.title,
          }));
        }

        options = options.map((option) =>
          typeof option === "string"
            ? {
                value: option,
                label: option,
              }
            : option,
        );

        const numericSource =
          isParentCategory || isChildCategory || isCollection;

        return (
          <select
            value={value ?? ""}
            onChange={(e) => {
              const raw = e.target.value;

              const nextValue =
                raw === "" ? null : numericSource ? Number(raw) : raw;

              if (isParentCategory) {
                setParentCategoryId(nextValue);

                handleContentChange("parent_category_id", nextValue);

                handleContentChange("category_id", null);

                return;
              }

              onChange(nextValue);
            }}
          >
            <option value="">
              {isParentCategory
                ? "همه دسته‌ها"
                : isChildCategory
                  ? "همه زیر دسته‌ها"
                  : "انتخاب کنید"}
            </option>

            {options.map((option, index) => (
              <option key={option.value ?? index} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );
      }

      case "text":
      default:
        return (
          <input
            type="text"
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value)}
          />
        );
    }
  };

  /*
   * جدا کردن اینپوت‌های غیر عکسی از عکسی:
   * غیر عکسی‌ها توی یک باکس جدا زیر هم چیده می‌شوند،
   * عکسی‌ها توی گرید خودشان.
   */
  const renderFieldsGroup = (fieldsList, getValue, getOnChange) => {
    const textFields = fieldsList.filter((field) => field.type !== "image");
    const imageFields = fieldsList.filter((field) => field.type === "image");

    const textStack = textFields.length > 0 && (
      <div className="se-fields-stack">
        {textFields.map((field) => (
          <div className="se-field" key={field.name}>
            <label>{field.label || field.name}</label>

            {renderField(field, getValue(field), getOnChange(field))}
          </div>
        ))}
      </div>
    );

    if (imageFields.length === 0) {
      return textStack;
    }

    return (
      <div className="se-fields-row">
        <div className="se-fields-images">
          {imageFields.map((field) => (
            <div className="se-field" key={field.name}>
              <label>{field.label || field.name}</label>

              {renderField(field, getValue(field), getOnChange(field))}
            </div>
          ))}
        </div>

        {textStack}
      </div>
    );
  };

  /*
   * اگر section وجود نداشت
   */
  if (!section) {
    return null;
  }

  /*
   * سکشن‌های نمایش محصولات (product_showcase_*) به‌جای
   * چیدمان معمولِ هدر + فیلدهای زیر هم، به شکل کارت فشرده
   * (شبیه ShowcaseCard قدیمی در HomeTab) نمایش داده می‌شوند.
   */
  if (isProductShowcase) {
    return (
      <div className="section-editor">
        <Toaster
          position="top-center"
          toastOptions={{
            duration: 3000,
            style: {
              fontFamily: "inherit",
              direction: "rtl",
              fontSize: "13.5px",
            },
          }}
        />

        <div className="se-showcase-card">
          <div className="se-showcase-card-head">
            <span className="se-showcase-badge">
              نمایش محصولات
              {typeof showcaseOrder === "number" && ` ${showcaseOrder}`}
            </span>

            <button
              type="button"
              className="esp-icon-btn esp-icon-btn--check"
              disabled={saving}
              onClick={saveSection}
              title="ذخیره این ویترین"
            >
              <Icon>{CHECK_ICON}</Icon>
            </button>
          </div>

          {fields.length === 0 ? (
            <div className="se-empty">
              هیچ فیلدی برای این سکشن تعریف نشده است.
            </div>
          ) : (
            renderFieldsGroup(
              fields,
              (field) => content[field.name],
              (field) => (value) => handleContentChange(field.name, value),
            )
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="section-editor">
      <Toaster
        position="top-center"
        toastOptions={{
          duration: 3000,
          style: {
            fontFamily: "inherit",
            direction: "rtl",
            fontSize: "13.5px",
          },
        }}
      />

      <div className="se-header">
        <div>
          <h2 className="se-title">{title}</h2>

          <p className="se-description">
            تنظیمات این بخش را از اینجا مدیریت کنید.
          </p>
        </div>
      </div>

      {!repeatable && (
        <div className="se-content">
          {fields.length === 0 ? (
            <div className="se-empty">
              هیچ فیلدی برای این سکشن تعریف نشده است.
            </div>
          ) : (
            renderFieldsGroup(
              fields,
              (field) => content[field.name],
              (field) => (value) => handleContentChange(field.name, value),
            )
          )}

          {fields.length > 0 && (
            <div className="se-actions">
              <button
                type="button"
                className="se-save-btn"
                disabled={saving}
                onClick={saveSection}
              >
                {/* <Icon className={"esp-save-btn"}>{CHECK_ICON}</Icon> */}

                {saving ? "در حال ذخیره..." : "ذخیره"}
              </button>
            </div>
          )}
        </div>
      )}

      {repeatable && (
        <div className="se-repeatable">
          {items.length === 0 && (
            <div className="se-empty">هنوز آیتمی اضافه نشده است.</div>
          )}

          <div className="se-repeatable-list">
            {items.map((item, index) => (
              <div
                className={
                  "se-repeatable-item" +
                  (dirtyItemIds.has(item.item_id)
                    ? " se-repeatable-item--dirty"
                    : "")
                }
                key={item.item_id}
              >
                <div className="se-repeatable-head">
                  <div className="se-repeatable-head-labels">
                    <span className="se-badge">آیتم {index + 1}</span>

                    {dirtyItemIds.has(item.item_id) && (
                      <span className="se-badge se-badge--unsaved">
                        تغییرات ذخیره نشده
                      </span>
                    )}
                  </div>

                  <div className="se-repeatable-actions">
                    <button
                      type="button"
                      className="esp-icon-btn esp-icon-btn--check"
                      onClick={() => saveItem(item)}
                      title="ذخیره"
                    >
                      <Icon>{CHECK_ICON}</Icon>
                    </button>

                    <button
                      type="button"
                      className="esp-icon-btn esp-icon-btn--remove"
                      onClick={() => removeItem(item.item_id)}
                      title="حذف"
                    >
                      <Icon>{TRASH_ICON}</Icon>
                    </button>
                  </div>
                </div>

                {renderFieldsGroup(
                  fields,
                  (field) => item.content_json?.[field.name],
                  (field) => (value) =>
                    updateItemField(item.item_id, field.name, value),
                )}
              </div>
            ))}
          </div>

          <button type="button" className="se-add-btn" onClick={addItem}>
            <Icon>{PLUS_ICON}</Icon>
            افزودن آیتم
          </button>
        </div>
      )}
    </div>
  );
}
